import { supabase } from '@/integrations/supabase/client'
import { toast } from 'sonner'

export interface UploadResult {
  url: string
  path: string
}

export class ImageUploadService {
  private static readonly BUCKET_NAME = 'blog-images'
  private static readonly MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB
  private static readonly ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']

  /**
   * Upload une image vers Supabase Storage
   */
  static async uploadImage(file: File, folder = 'articles'): Promise<UploadResult> {
    try {
      // Validation du fichier
      this.validateFile(file)

      // Générer un nom de fichier unique
      const timestamp = Date.now()
      const randomId = Math.random().toString(36).substring(2, 15)
      const fileExtension = file.name.split('.').pop()?.toLowerCase()
      const fileName = `${timestamp}_${randomId}.${fileExtension}`
      const filePath = `${folder}/${fileName}`

      console.log('Uploading image:', { fileName, filePath, size: file.size })

      // Upload vers Supabase Storage
      const { data, error } = await supabase.storage
        .from(this.BUCKET_NAME)
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        })

      if (error) {
        console.error('Upload error:', error)
        throw new Error(`Erreur d'upload: ${error.message}`)
      }

      // Obtenir l'URL publique
      const { data: urlData } = supabase.storage
        .from(this.BUCKET_NAME)
        .getPublicUrl(filePath)

      console.log('Upload successful:', { path: data.path, url: urlData.publicUrl })

      return {
        url: urlData.publicUrl,
        path: data.path
      }
    } catch (error: any) {
      console.error('Image upload failed:', error)
      throw new Error(`Échec de l'upload: ${error.message}`)
    }
  }

  /**
   * Upload multiple images
   */
  static async uploadMultipleImages(files: File[], folder = 'articles'): Promise<UploadResult[]> {
    const results: UploadResult[] = []
    const errors: string[] = []

    for (const file of files) {
      try {
        const result = await this.uploadImage(file, folder)
        results.push(result)
      } catch (error: any) {
        errors.push(`${file.name}: ${error.message}`)
      }
    }

    if (errors.length > 0) {
      toast.error(`Certains uploads ont échoué: ${errors.join(', ')}`)
    }

    return results
  }

  /**
   * Supprimer une image
   */
  static async deleteImage(path: string): Promise<void> {
    try {
      const { error } = await supabase.storage
        .from(this.BUCKET_NAME)
        .remove([path])

      if (error) {
        throw new Error(`Erreur de suppression: ${error.message}`)
      }

      console.log('Image deleted successfully:', path)
    } catch (error: any) {
      console.error('Delete error:', error)
      throw error
    }
  }

  /**
   * Redimensionner une image côté client avant upload
   */
  static async resizeImage(file: File, maxWidth = 1200, maxHeight = 800, quality = 0.8): Promise<File> {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      const img = new Image()

      img.onload = () => {
        // Calculer les nouvelles dimensions
        let { width, height } = img
        
        if (width > maxWidth) {
          height = (height * maxWidth) / width
          width = maxWidth
        }
        
        if (height > maxHeight) {
          width = (width * maxHeight) / height
          height = maxHeight
        }

        // Redimensionner
        canvas.width = width
        canvas.height = height
        ctx?.drawImage(img, 0, 0, width, height)

        // Convertir en Blob puis en File
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const resizedFile = new File([blob], file.name, {
                type: file.type,
                lastModified: Date.now()
              })
              resolve(resizedFile)
            } else {
              resolve(file) // Fallback to original file
            }
          },
          file.type,
          quality
        )
      }

      img.src = URL.createObjectURL(file)
    })
  }

  /**
   * Valider un fichier
   */
  private static validateFile(file: File): void {
    if (!file) {
      throw new Error('Aucun fichier sélectionné')
    }

    if (file.size > this.MAX_FILE_SIZE) {
      throw new Error(`Fichier trop volumineux. Taille maximum: ${this.MAX_FILE_SIZE / 1024 / 1024}MB`)
    }

    if (!this.ALLOWED_TYPES.includes(file.type)) {
      throw new Error(`Type de fichier non supporté. Types autorisés: ${this.ALLOWED_TYPES.join(', ')}`)
    }
  }

  /**
   * Créer une URL temporaire pour prévisualisation
   */
  static createPreviewUrl(file: File): string {
    return URL.createObjectURL(file)
  }

  /**
   * Libérer une URL temporaire
   */
  static revokePreviewUrl(url: string): void {
    URL.revokeObjectURL(url)
  }

  /**
   * Obtenir les informations d'une image uploadée
   */
  static async getImageInfo(path: string) {
    try {
      const { data, error } = await supabase.storage
        .from(this.BUCKET_NAME)
        .list(path.split('/').slice(0, -1).join('/'), {
          search: path.split('/').pop()
        })

      if (error) throw error

      return data?.[0] || null
    } catch (error) {
      console.error('Error getting image info:', error)
      return null
    }
  }

  /**
   * Vérifier si le bucket existe et le créer si nécessaire
   */
  static async ensureBucketExists(): Promise<void> {
    try {
      const { data: buckets, error } = await supabase.storage.listBuckets()
      
      if (error) {
        console.error('Error listing buckets:', error)
        return
      }

      const bucketExists = buckets?.some(bucket => bucket.name === this.BUCKET_NAME)
      
      if (!bucketExists) {
        console.log('Creating blog-images bucket...')
        const { error: createError } = await supabase.storage.createBucket(this.BUCKET_NAME, {
          public: true,
          allowedMimeTypes: this.ALLOWED_TYPES,
          fileSizeLimit: this.MAX_FILE_SIZE
        })
        
        if (createError) {
          console.error('Error creating bucket:', createError)
        } else {
          console.log('Blog images bucket created successfully')
        }
      }
    } catch (error) {
      console.error('Error ensuring bucket exists:', error)
    }
  }
}

// Hook pour utiliser le service d'upload
export const useImageUpload = () => {
  const uploadImage = async (file: File, folder?: string): Promise<UploadResult> => {
    // S'assurer que le bucket existe
    await ImageUploadService.ensureBucketExists()
    
    // Redimensionner l'image si elle est trop grande
    const resizedFile = await ImageUploadService.resizeImage(file)
    
    // Upload
    return await ImageUploadService.uploadImage(resizedFile, folder)
  }

  const uploadMultipleImages = async (files: File[], folder?: string): Promise<UploadResult[]> => {
    await ImageUploadService.ensureBucketExists()
    
    // Redimensionner toutes les images
    const resizedFiles = await Promise.all(
      Array.from(files).map(file => ImageUploadService.resizeImage(file))
    )
    
    return await ImageUploadService.uploadMultipleImages(resizedFiles, folder)
  }

  const deleteImage = async (path: string): Promise<void> => {
    return await ImageUploadService.deleteImage(path)
  }

  return {
    uploadImage,
    uploadMultipleImages,
    deleteImage,
    createPreviewUrl: ImageUploadService.createPreviewUrl,
    revokePreviewUrl: ImageUploadService.revokePreviewUrl
  }
} 